class Hamburger extends HTMLElement {
  constructor() {
    super();
    this.header = this.closest('header');
    this.mouseEvent = this.dataset.event;
    this.first_levels = Array.from(this.querySelectorAll('[data-first-level-item]'));
    this.second_levels = Array.from(this.querySelectorAll('[data-child-link]'));
    this.third_levels = Array.from(this.querySelectorAll('[data-grand-child-link]'));
    this.back_overlay = this.header?.querySelector('[data-backdrop-overlay]');
    this.lastFocusedTrigger = null;
    this.triggerStack = [];

    this.header?.querySelector('[data-toggle]').addEventListener('click', this.open.bind(this));
    this.querySelector('[data-close]').addEventListener('click', this.close.bind(this));

    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.addEventListener('click', this.handleClick);
    if (this.mouseEvent == 'on-hover') {
      this.handleHover = this.handleHover.bind(this);
      this.addEventListener('mouseover', this.handleHover);
    }
    if (this.mouseEvent == 'on-hover') {
      this.addEventListener('focusin', (event) => {
        const secondHoverTrigger = event.target.closest('[is="has-hovered-second-menu"]');
        if (secondHoverTrigger) {
          // Simulate Enter key press to reuse full submenu logic
          this.handleClick({ target: secondHoverTrigger, key: 'Enter', preventDefault() {} });
        }
      });
    }



    this.addEventListener('keydown', this.handleKeyDown);
    if (!Shopify.designMode) {
      this.addEventListener('focusout', this.handleBlur.bind(this));
    }
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.handleCursorMove();
  }

  handleKeyDown(event) {
    const secondTrigger = event.target.closest('[is="has-second-menu"]');
    const thirdTrigger = event.target.closest('[is="has-third-menu"]');
    const backButton = event.target.closest('[data-back-to-sub-menu]');

    if (event.key === 'Enter') {
      // Prevent default only for menu triggers to avoid double handling
      if (secondTrigger || thirdTrigger || backButton) {
        event.preventDefault();
        this.handleClick(event);
      }
    } else if (event.key === 'Tab' && event.shiftKey) {
      const activeElement = document.activeElement;
      const currentSubmenu = activeElement.closest('[data-submenu-wrapper], [data-submenu-child-wrapper]');
      
      if (currentSubmenu) {
        const firstFocusable = this.getFirstFocusable(currentSubmenu);
        if (firstFocusable === activeElement) {
          event.preventDefault();
          const trigger = this.triggerStack.find(t => t.submenu === currentSubmenu);
          if (trigger) {
            trigger.element.focus();
          }
        }
      }
    }
  }

  getFirstFocusable(element) {
    const focusable = element.querySelectorAll('a[href], button, [tabindex="0"]');
    return focusable.length > 0 ? focusable[0] : null;
  }

  handleHover(event) {
    const secondHoverTrigger = event.target.closest('[is="has-hovered-second-menu"]');
    if (secondHoverTrigger) {
      event.preventDefault();
      const linkWrapper = secondHoverTrigger.closest('[data-menus]');
      const submenu = linkWrapper?.querySelector('[data-second-level]');
      const openSubmenu = submenu?.querySelector(`[data-submenu-wrapper="${secondHoverTrigger.dataset.link}"]`);

      if (submenu) {
        submenu.classList.add('open');
      }
      submenu?.querySelectorAll('[data-submenu-wrapper], [data-submenu-child-wrapper]').forEach((element) => {
        element.classList.remove('open');
      });
      submenu?.querySelectorAll('[data-child-link], [data-grand-child-link]').forEach((element) => {
        element.classList.remove('animate');
      });
      if (openSubmenu) {
        openSubmenu.classList.add('open');
        this.animateIn(openSubmenu?.querySelectorAll('[data-child-link]'));
      }
      return;
    }
  }

  handleClick(event) {
    // Handle submenu triggers
    const secondTrigger = event.target.closest('[is="has-second-menu"]');
    const thirdTrigger = event.target.closest('[is="has-third-menu"]');
    
    if (secondTrigger && this.mouseEvent == 'click') {
      event.preventDefault();
      this.lastFocusedTrigger = secondTrigger;
      const linkWrapper = secondTrigger.closest('[data-menus]');
      const submenu = linkWrapper?.querySelector('[data-second-level]');
      const openSubmenu = submenu?.querySelector(`[data-submenu-wrapper="${secondTrigger.dataset.link}"]`);

      if (submenu) {
        submenu.classList.add('open');
      }
      submenu?.querySelectorAll('[data-submenu-wrapper], [data-submenu-child-wrapper]').forEach((element) => {
        element.classList.remove('open');
      });
      submenu?.querySelectorAll('[data-child-link], [data-grand-child-link]').forEach((element) => {
        element.classList.remove('animate');
      });
      if (openSubmenu) {
        openSubmenu.classList.add('open');
        this.animateIn(openSubmenu?.querySelectorAll('[data-child-link]'));
        // Push to trigger stack
        this.triggerStack.push({
          element: secondTrigger,
          submenu: openSubmenu
        });
        // Focus on first focusable element in the opened submenu
        setTimeout(() => {
          const firstFocusable = this.getFirstFocusable(openSubmenu);
          if (firstFocusable) firstFocusable.focus();
        }, 50);
      }
      return;
    }

    if (thirdTrigger) {
      event.preventDefault();
      this.lastFocusedTrigger = thirdTrigger;
      const submenu = thirdTrigger.closest('[data-child-link]');
      const submenuWrapper = thirdTrigger.closest('[data-submenu-wrapper]');

      // Check if the current submenu is already open
      const isAlreadyOpen = submenu.classList.contains('open');
      submenu.style.setProperty(
        '--hamburger-submenu-menu-height',
        `${submenu?.querySelector('[data-submenu-child-wrapper]')?.getBoundingClientRect().height}px`
      );
      // Close all submenus first
      submenuWrapper?.querySelectorAll('[data-child-link]').forEach((element) => {
        element.classList.remove('open');
      });

      // Toggle only if it was NOT already open before
      if (!isAlreadyOpen) {
        submenu.classList.add('open');
        // Push to trigger stack
        this.triggerStack.push({
          element: thirdTrigger,
          submenu: submenu
        });
        // Focus on first focusable element in the opened submenu
        setTimeout(() => {
          const firstFocusable = this.getFirstFocusable(submenu);
          if (firstFocusable) firstFocusable.focus();
        }, 50);
      } else if (this.lastFocusedTrigger) {
        // If closing, return focus to the trigger
        this.lastFocusedTrigger.focus();
        // Remove from trigger stack
        this.triggerStack = this.triggerStack.filter(item => item.submenu !== submenu);
      }
    }

    // Handle back buttons
    const backButton = event.target.closest('[data-back-to-sub-menu]');
    if (backButton) {
      event.preventDefault();
      const submenu = backButton.closest('[data-submenu-child-wrapper]');
      if (submenu) {
        submenu.classList.remove('open');
        this.animateOut(this.third_levels);
        // Find and focus the trigger that opened this submenu
        const triggerItem = this.triggerStack.find(item => item.submenu === submenu);
        if (triggerItem) {
          triggerItem.element.focus();
          // Remove from trigger stack
          this.triggerStack = this.triggerStack.filter(item => item.submenu !== submenu);
        }
      }
      return;
    }
  }

  open() {
    this.activeElement = this.header.querySelector('[data-toggle]');
    this.setActiveElement(this.activeElement);
    lockBodyScroll();
    if (this.classList.contains('close')) {
      this.classList.remove('close');
    }
    this.classList.add('open');
    setTimeout(() => {
      this.animateIn(this.first_levels);
    }, 500);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this;
        const focusElement = this.querySelector('[data-close]');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  handleBlur(event) {
    setTimeout(() => {
      if (this.classList.contains('open') && !this.contains(document.activeElement)) {
        this.querySelector('[data-close]').focus();
      }
    }, 10);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  close() {
    this.classList.add('close');
    this.classList.remove('open');
    setTimeout(() => {
      removeTrapFocus(this.activeElement); 
      this.activeElement.focus();
    }, 500);
    this?.querySelectorAll('[data-submenu-wrapper], [data-submenu-child-wrapper], [data-second-level]').forEach(
      (element) => {
        element.classList.remove('open');
      }
    );
    this.animateOut(this.first_levels);
    this.animateOut(this.second_levels);
    this.animateOut(this.third_levels);
    this.triggerStack = []; // Clear trigger stack when closing menu
    unlockBodyScroll();
  }

  animateIn(elements) {
    elements.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate');
      }, index * 50);
    });
  }

  animateOut(elements) {
    elements.forEach((item, index) => {
      setTimeout(() => {
        item.classList.remove('animate');
      }, index * 50);
    });
  }

  handleCursorMove() {
    if (!this.back_overlay) return;

    const cursorWrapper = this.header.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.back_overlay.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });

    this.back_overlay.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.back_overlay.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });

    this.header.addEventListener('click', (e) => {
      if (e.target.closest('[data-backdrop-overlay]')) {
        this.close();
      }
    });
  }
}

customElements.define('header-hamburger', Hamburger);